import { useState, useCallback, useEffect, useRef } from "react";
import {
    fetchRecipesApi,
    createRecipeApi,
    updateRecipeApi,
    deleteRecipeApi,
    optimizeRecipeApi,
    publishRecipeApi,
    unpublishRecipeApi,
    logFeedbackApi,
    fetchFeedbackApi,
    fetchRecipeByIdApi,
    toggleFavoriteApi
} from "../services/recipeService";

export function useRecipes({ token }) {
    // Separate Lists
    const [publicRecipes, setPublicRecipes] = useState([]);
    const [privateRecipes, setPrivateRecipes] = useState([]);

    // Pagination State
    const [publicPagination, setPublicPagination] = useState({ page: 1, hasMore: true, loading: false });
    const [privatePagination, setPrivatePagination] = useState({ page: 1, hasMore: true, loading: false });

    // Filter State
    const [filters, setFilters] = useState({
        search: "",
        tags: [],
        sort: "newest" // 'newest', 'popularity'
    });

    const [loading, setLoading] = useState(false); // Global loading for actions
    const [error, setError] = useState("");

    // Cache for getById to avoid redundant fetches if not in list
    const recipeCache = useRef(new Map());

    // --- Loading Logic ---

    const loadRecipes = useCallback(async (type, page, currentFilters, reset = false) => {
        const isPublic = type === 'public';
        const setPagination = isPublic ? setPublicPagination : setPrivatePagination;
        const setList = isPublic ? setPublicRecipes : setPrivateRecipes;

        setPagination(prev => ({ ...prev, loading: true }));
        try {
            const params = {
                type,
                page,
                limit: 10,
                search: currentFilters.search,
                tags: currentFilters.tags,
                sort: currentFilters.sort,
                offset: (page - 1) * 10
            };

            const data = await fetchRecipesApi({ token, params });
            // Handle both array (legacy) and object (paginated)
            const newRecipes = Array.isArray(data) ? data : (data.recipes || []);
            const total = data.total || newRecipes.length;

            if (reset) {
                setList(newRecipes);
            } else {
                setList(prev => [...prev, ...newRecipes]);
            }

            setPagination(prev => ({
                ...prev,
                page,
                hasMore: newRecipes.length >= 10 && (page * 10 < total), // Basic check
                loading: false
            }));

            // Update Cache
            newRecipes.forEach(r => recipeCache.current.set(r.id, r));

        } catch (err) {
            console.error(err);
            setPagination(prev => ({ ...prev, loading: false }));
        }
    }, [token]);

    // Initial Load & Filter Change
    useEffect(() => {
        // Reset and load both
        setPublicRecipes([]);
        setPrivateRecipes([]);
        setPublicPagination(p => ({ ...p, page: 1, hasMore: true }));
        setPrivatePagination(p => ({ ...p, page: 1, hasMore: true }));

        loadRecipes('public', 1, filters, true);
        loadRecipes('private', 1, filters, true);
    }, [filters, loadRecipes]); // Re-run when filters change

    // Load More Handlers
    const loadMorePublic = () => {
        if (!publicPagination.hasMore || publicPagination.loading) return;
        loadRecipes('public', publicPagination.page + 1, filters, false);
    };

    const loadMorePrivate = () => {
        if (!privatePagination.hasMore || privatePagination.loading) return;
        loadRecipes('private', privatePagination.page + 1, filters, false);
    };

    // --- Actions ---

    const getRecipeById = useCallback(async (id) => {
        // 1. Check Lists
        const inPublic = publicRecipes.find(r => r.id === id);
        if (inPublic) return inPublic;
        const inPrivate = privateRecipes.find(r => r.id === id);
        if (inPrivate) return inPrivate;

        // 2. Check Cache
        if (recipeCache.current.has(id)) return recipeCache.current.get(id);

        // 3. Fetch
        try {
            const recipe = await fetchRecipeByIdApi({ token, id });
            if (recipe) recipeCache.current.set(id, recipe);
            return recipe;
        } catch (e) {
            console.error("Failed to fetch recipe by ID", id);
            return null;
        }
    }, [publicRecipes, privateRecipes, token]);

    const addRecipe = async (recipe) => {
        setLoading(true);
        try {
            const newRecipe = await createRecipeApi({ token, recipe });
            // Add to private list
            setPrivateRecipes(prev => [newRecipe, ...prev]);
            recipeCache.current.set(newRecipe.id, newRecipe);
            return newRecipe;
        } catch (err) {
            console.error(err);
            setError("Failed to create recipe.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const updateRecipe = async (id, recipeData) => {
        setLoading(true);
        try {
            const updated = await updateRecipeApi({ token, id, recipe: recipeData });

            // Update in lists if present
            setPrivateRecipes(prev => prev.map(r => r.id === id ? updated : r));
            setPublicRecipes(prev => prev.map(r => r.id === id ? updated : r));
            recipeCache.current.set(id, updated);

            return updated;
        } catch (err) {
            console.error(err);
            setError("Failed to update recipe.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const removeRecipe = async (id) => {
        setLoading(true);
        try {
            await deleteRecipeApi({ token, id });
            setPrivateRecipes(prev => prev.filter(r => r.id !== id));
            setPublicRecipes(prev => prev.filter(r => r.id !== id));
            recipeCache.current.delete(id);
        } catch (err) {
            console.error(err);
            setError("Failed to delete recipe.");
        } finally {
            setLoading(false);
        }
    };

    // ... (Keep existing optimize/publish methods, updating lists accordingly)
    const optimizeRecipe = async (idOrRecipe, prompt) => {
        setLoading(true);
        try {
            let optimized;
            if (typeof idOrRecipe === 'object' && idOrRecipe !== null) {
                // Optimization of content (not saved yet)
                optimized = await import("../services/recipeService").then(m =>
                    m.optimizeRecipeContentApi({ token, recipe: idOrRecipe, prompt })
                );
            } else {
                optimized = await optimizeRecipeApi({ token, id: idOrRecipe, prompt });
            }
            return optimized;
        } catch (err) {
            console.error(err);
            setError("Failed to optimize recipe.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const publishRecipe = async (id) => {
        setLoading(true);
        try {
            const updated = await publishRecipeApi({ token, id });
            // Move from Private to Public (or keep in both if logic says so?)
            // Usually published recipes appear in Public. Owner still sees them.
            // Let's update both lists.
            setPrivateRecipes(prev => prev.map(r => r.id === id ? updated : r));

            // If it matches public filters, add it? Or just reload public?
            // Simpler to reload public or manually add if date matches. 
            // For now, let's just reload public to be safe or ignore if sort is tricky.
            // Or unshift to public if sort is newest.
            if (filters.sort === 'newest') {
                setPublicRecipes(prev => [updated, ...prev]);
            }

            recipeCache.current.set(id, updated);
            return updated;
        } catch (err) {
            console.error(err);
            setError("Failed to publish recipe.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const unpublishRecipe = async (id) => {
        setLoading(true);
        try {
            const updated = await unpublishRecipeApi({ token, id });
            setPrivateRecipes(prev => prev.map(r => r.id === id ? updated : r));
            setPublicRecipes(prev => prev.filter(r => r.id !== id));
            recipeCache.current.set(id, updated);
            return updated;
        } catch (err) {
            console.error(err);
            setError("Failed to unpublish recipe.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const toggleFavorite = async (recipeId) => {
        try {
            const result = await toggleFavoriteApi({ token, id: recipeId });
            const updateFav = (recipe) =>
                recipe.id === recipeId ? { ...recipe, isFavorited: result.isFavorited } : recipe;
            setPublicRecipes(prev => prev.map(updateFav));
            setPrivateRecipes(prev => prev.map(updateFav));
            const cached = recipeCache.current.get(recipeId);
            if (cached) recipeCache.current.set(recipeId, { ...cached, isFavorited: result.isFavorited });
            return result;
        } catch (err) {
            console.error("Failed to toggle favorite:", err);
        }
    };

    // Passthroughs
    const logFeedback = async (id, feedback) => logFeedbackApi({ token, id, feedback });
    const getFeedback = async (id) => fetchFeedbackApi({ token, id });

    return {
        // Lists
        publicRecipes,
        privateRecipes,

        // Pagination
        publicPagination,
        privatePagination,
        loadMorePublic,
        loadMorePrivate,

        // Filters
        filters,
        setFilters,

        // Actions
        loading,
        error,
        getRecipeById,
        addRecipe,
        updateRecipe,
        removeRecipe,
        optimizeRecipe,
        publishRecipe,
        unpublishRecipe,
        logFeedback,
        getFeedback,
        toggleFavorite,

        // Backward compatibility (if needed) - "recipes" usually meant all. 
        // We should warn or remove it. Component updates required.
        recipes: [...privateRecipes, ...publicRecipes],
    };
}
